import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { toPng } from 'html-to-image';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import mermaid from 'mermaid';
// Pastels from group members (same as project/utils/constants)
const PASTEL_HEX = ['#F5C8BD', '#E9BACC', '#D0AAB0', '#B599C1', '#ABACCB', '#9FBFDD', '#97CCDC', '#83C4B4', '#A9D194', '#D5E4AD', '#FAEEAE', '#F5BE6B'];

/** Darken a color by a factor (0–1). Accepts hex or rgb(). Used for stroke = darker variant of fill. */
function darkenColor(color, amount = 0.25) {
  if (!color || color === 'none') return color;
  let r, g, b;
  const hex = color.match(/^#([0-9a-f]{6})$/i);
  const rgb = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (hex) {
    r = parseInt(hex[1].slice(0, 2), 16);
    g = parseInt(hex[1].slice(2, 4), 16);
    b = parseInt(hex[1].slice(4, 6), 16);
  } else if (rgb) {
    r = +rgb[1];
    g = +rgb[2];
    b = +rgb[3];
  } else {
    return color;
  }
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function luminance(cssColor) {
  if (!cssColor) return null;
  const rgb = cssColor.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return (0.299 * +rgb[1] + 0.587 * +rgb[2] + 0.114 * +rgb[3]) / 255;
  const hex6 = cssColor.match(/#([0-9a-f]{6})/i);
  if (hex6) return (0.299 * parseInt(hex6[1].slice(0,2),16) + 0.587 * parseInt(hex6[1].slice(2,4),16) + 0.114 * parseInt(hex6[1].slice(4,6),16)) / 255;
  const hex3 = cssColor.match(/#([0-9a-f]{3})/i);
  if (hex3) { const h = hex3[1]; return (0.299 * parseInt(h[0]+h[0],16) + 0.587 * parseInt(h[1]+h[1],16) + 0.114 * parseInt(h[2]+h[2],16)) / 255; }
  return null;
}

function contrastColor(bgLum, fallbackBgLum) {
  const lum = bgLum ?? fallbackBgLum ?? 0.5;
  return lum > 0.5 ? '#111827' : '#f4f4f5';
}

function getComputedFill(el) {
  try {
    const cs = window.getComputedStyle(el);
    return cs.fill && cs.fill !== 'none' ? cs.fill : null;
  } catch { return null; }
}

/**
 * After Mermaid SVG is in the live DOM, fix text contrast by reading the computed
 * fill of each node's shape and setting text color accordingly.
 * Targets Mermaid's actual class structure: .node > rect + .label > foreignObject > .nodeLabel
 */
function fixSvgContrastInDom(container, fallbackBg) {
  if (!container) return;
  const svgEl = container.querySelector('svg');
  if (!svgEl) return;

  const fallbackLum = luminance(fallbackBg);

  // 1. Node labels (.node groups contain a shape + foreignObject with .nodeLabel)
  svgEl.querySelectorAll('.node, .nodes .node').forEach((nodeG) => {
    const shape = nodeG.querySelector('rect, polygon, ellipse, circle, path');
    const fill = shape ? getComputedFill(shape) : null;
    const lum = luminance(fill);
    const color = contrastColor(lum, fallbackLum);
    nodeG.querySelectorAll('.nodeLabel, .label span, .label p, .label div').forEach((el) => {
      el.style.setProperty('color', color, 'important');
    });
    nodeG.querySelectorAll('text, tspan').forEach((el) => {
      el.setAttribute('fill', color);
    });
  });

  // 2. Edge labels — background is always a light pastel, so text is always dark
  svgEl.querySelectorAll('.edgeLabel').forEach((edgeG) => {
    const bg = edgeG.querySelector('rect');
    const fill = bg ? getComputedFill(bg) : null;
    // Use rect fill if available; otherwise force dark text since pastel bg is light
    const color = fill ? contrastColor(luminance(fill), fallbackLum) : '#111827';
    edgeG.querySelectorAll('span, p, div, .edgeLabel').forEach((el) => {
      el.style.setProperty('color', color, 'important');
    });
    edgeG.querySelectorAll('text, tspan').forEach((el) => {
      el.setAttribute('fill', color);
    });
  });

  // 3. Cluster / subgraph labels
  svgEl.querySelectorAll('.cluster-label').forEach((cl) => {
    const cluster = cl.closest('.cluster');
    const shape = cluster ? cluster.querySelector('rect, polygon, path') : null;
    const fill = shape ? getComputedFill(shape) : null;
    const lum = luminance(fill);
    const color = contrastColor(lum, fallbackLum);
    cl.querySelectorAll('span, p, div').forEach((el) => {
      el.style.setProperty('color', color, 'important');
    });
    cl.querySelectorAll('text, tspan').forEach((el) => {
      el.setAttribute('fill', color);
    });
  });

  // 4. Any remaining SVG <text> not inside .node or .edgeLabel (axis labels, titles, etc.)
  svgEl.querySelectorAll('text').forEach((t) => {
    if (t.closest('.node') || t.closest('.edgeLabel') || t.closest('.cluster-label')) return;
    const color = contrastColor(fallbackLum, fallbackLum);
    t.setAttribute('fill', color);
    t.querySelectorAll('tspan').forEach((ts) => ts.setAttribute('fill', color));
  });

  // 5. Any remaining foreignObject spans not already handled
  svgEl.querySelectorAll('foreignObject').forEach((fo) => {
    if (fo.closest('.node') || fo.closest('.edgeLabel') || fo.closest('.cluster-label')) return;
    const color = contrastColor(fallbackLum, fallbackLum);
    fo.querySelectorAll('span, p, div').forEach((el) => {
      el.style.setProperty('color', color, 'important');
    });
  });
}

/** Convert polygon points to path with rounded corners. Returns path d string. */
function polygonToRoundedPath(pointsStr, r = 12) {
  const pts = pointsStr.trim().split(/\s+/).map((p) => {
    const [x, y] = p.split(',').map(Number);
    return { x: x || 0, y: y || 0 };
  });
  if (pts.length < 3) return '';
  const n = pts.length;
  const r2 = Math.min(r, 20); // cap radius

  const corners = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];

    const v1x = p0.x - p1.x;
    const v1y = p0.y - p1.y;
    const v2x = p2.x - p1.x;
    const v2y = p2.y - p1.y;
    const len1 = Math.hypot(v1x, v1y) || 1;
    const len2 = Math.hypot(v2x, v2y) || 1;

    const u1x = v1x / len1;
    const u1y = v1y / len1;
    const u2x = v2x / len2;
    const u2y = v2y / len2;

    const angle = Math.acos(Math.max(-1, Math.min(1, u1x * u2x + u1y * u2y)));
    const cut = angle < 0.01 ? 0 : r2 / Math.tan(angle / 2);
    const cut1 = Math.min(cut, len1 / 2);
    const cut2 = Math.min(cut, len2 / 2);

    corners.push({
      before: { x: p1.x + u1x * cut1, y: p1.y + u1y * cut1 },
      vertex: p1,
      after: { x: p1.x + u2x * cut2, y: p1.y + u2y * cut2 },
      hasRound: cut1 > 0.1 || cut2 > 0.1,
    });
  }

  let d = `M ${corners[0].before.x} ${corners[0].before.y}`;
  for (let i = 0; i < n; i++) {
    const c = corners[i];
    const next = corners[(i + 1) % n];
    if (c.hasRound) {
      d += ` Q ${c.vertex.x} ${c.vertex.y} ${c.after.x} ${c.after.y}`;
    } else {
      d += ` L ${c.after.x} ${c.after.y}`;
    }
    d += ` L ${next.before.x} ${next.before.y}`;
  }
  return d + ' Z';
}

/** Apply squircle-like rounded corners to node shapes. Mermaid doesn't expose this in theme. */
function applySquircleToNodes(container) {
  if (!container) return;
  const svgEl = container.querySelector('svg');
  if (!svgEl) return;

  const radius = 16; // Rounded corner radius for squircle-like effect on nodes

  // Node rects (flowchart rectangles, etc.)
  svgEl.querySelectorAll('.node rect, .nodes .node rect').forEach((rect) => {
    rect.setAttribute('rx', radius);
    rect.setAttribute('ry', radius);
  });

  // Diamond/rhombus nodes use polygon - convert to path with rounded corners
  svgEl.querySelectorAll('.node polygon, .nodes .node polygon').forEach((poly) => {
    if (poly.closest('.edgeLabel')) return;
    const points = poly.getAttribute('points');
    if (!points) return;
    const pathD = polygonToRoundedPath(points, 12);
    if (!pathD) return;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathD);
    ['fill', 'stroke', 'stroke-width', 'transform', 'id'].forEach((attr) => {
      const v = poly.getAttribute(attr);
      if (v) path.setAttribute(attr, v);
    });
    path.setAttribute('stroke-width', path.getAttribute('stroke-width') || '1');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-linecap', 'round');
    if (poly.className?.baseVal) path.setAttribute('class', poly.className.baseVal);

    poly.parentNode?.replaceChild(path, poly);
  });

  // Pill/stadium/roundedRect nodes use path - round corners via stroke (darker variant of fill)
  svgEl.querySelectorAll('.node path, .nodes .node path').forEach((path) => {
    if (path.closest('.edgeLabel')) return;
    const fill = path.getAttribute('fill') || path.style.fill ||
      (typeof getComputedStyle !== 'undefined' ? getComputedStyle(path).fill : 'currentColor');
    const strokeColor = (fill && fill !== 'none') ? darkenColor(fill) : (fill || 'currentColor');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke', strokeColor);
    path.setAttribute('stroke-width', '1');
  });

  // Cluster/subgraph rects - stroke = darker variant of fill
  svgEl.querySelectorAll('.cluster rect').forEach((rect) => {
    rect.setAttribute('rx', radius);
    rect.setAttribute('ry', radius);
    const fill = rect.getAttribute('fill') || getComputedStyle(rect).fill;
    if (fill && fill !== 'none') {
      rect.setAttribute('stroke', darkenColor(fill));
    }
  });

  // Edge label rects (the "55% convert" etc. blocks on arrows) - stroke = darker variant of fill
  svgEl.querySelectorAll('.edgeLabel rect, .edgeLabel .labelBkg rect, g.edgeLabel > rect').forEach((rect) => {
    rect.setAttribute('rx', radius);
    rect.setAttribute('ry', radius);
    const fill = rect.getAttribute('fill') || getComputedStyle(rect).fill;
    if (fill && fill !== 'none') {
      rect.setAttribute('stroke', darkenColor(fill));
    }
  });
  // Edge labels: rect is often a sibling of foreignObject inside a g
  svgEl.querySelectorAll('g').forEach((g) => {
    if (g.querySelector('foreignObject .edgeLabel')) {
      const r = g.querySelector('rect');
      if (r) {
        r.setAttribute('rx', radius);
        r.setAttribute('ry', radius);
      }
    }
  });

  // Note rects (sticky notes) - stroke = darker variant of fill
  svgEl.querySelectorAll('.note rect').forEach((rect) => {
    rect.setAttribute('rx', radius);
    rect.setAttribute('ry', radius);
    const fill = rect.getAttribute('fill') || getComputedStyle(rect).fill;
    if (fill && fill !== 'none') {
      rect.setAttribute('stroke', darkenColor(fill));
    }
  });
}

/**
 * Color nodes by their position along the primary flow axis so nodes at the
 * same level share a color, creating a gradient that follows the diagram flow.
 */
function applyPastelColorsToNodes(container) {
  if (!container) return;
  const svgEl = container.querySelector('svg');
  if (!svgEl) return;

  const nodes = Array.from(svgEl.querySelectorAll('.node, .nodes .node'));
  if (!nodes.length) return;

  // Get positions from the transform attribute (always present in Mermaid SVG,
  // unlike getBBox which requires layout and returns zeros before paint)
  const positioned = nodes.map((nodeG) => {
    const t = nodeG.getAttribute('transform') || '';
    const m = t.match(/translate\s*\(\s*([-\d.]+)[,\s]\s*([-\d.]+)\s*\)/);
    return { nodeG, cx: m ? parseFloat(m[1]) : 0, cy: m ? parseFloat(m[2]) : 0 };
  });

  const xs = positioned.map((d) => d.cx);
  const ys = positioned.map((d) => d.cy);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const xSpread = xMax - xMin;
  const ySpread = yMax - yMin;

  // Use the axis with more spread as the primary flow direction
  const isHorizontal = xSpread >= ySpread;
  const spread = isHorizontal ? xSpread : ySpread;
  const minPos = isHorizontal ? xMin : yMin;
  const NUM_BUCKETS = Math.min(PASTEL_HEX.length, 8);

  positioned.forEach(({ nodeG, cx, cy }) => {
    const pos = isHorizontal ? cx : cy;
    const bucket = spread > 0
      ? Math.max(0, Math.min(NUM_BUCKETS - 1, Math.round((pos - minPos) / spread * (NUM_BUCKETS - 1))))
      : 0;
    const color = PASTEL_HEX[bucket];
    const strokeColor = darkenColor(color);
    const shape = nodeG.querySelector('rect, polygon, path, ellipse, circle');
    if (shape) {
      shape.setAttribute('fill', color);
      shape.style.fill = color;
      shape.setAttribute('stroke', strokeColor);
      shape.style.stroke = strokeColor;
    }
  });
}

/** Add padding and rounded corners to edge labels. Resize foreignObject to fit. */
function applyEdgeLabelStyle(container) {
  if (!container) return;
  const padding = 6;
  const radius = 10;

  container.querySelectorAll('.labelBkg').forEach((div) => {
    if (div.closest('.node')) return; // skip node labels
    const fo = div.closest('foreignObject');
    const labelG = div.closest('g.label');
    if (!fo) return;

    div.querySelectorAll('.edgeLabel, .edgeLabel p, .edgeLabel span').forEach((el) => {
      el.style.setProperty('background-color', 'transparent', 'important');
    });

    const oldW = parseFloat(fo.getAttribute('width')) || 80;
    const oldH = parseFloat(fo.getAttribute('height')) || 24;
    const newW = oldW + padding * 2;
    const newH = oldH + padding * 2;

    div.style.padding = `${padding}px`;
    div.style.borderRadius = `${radius}px`;
    div.style.boxSizing = 'border-box';

    fo.setAttribute('width', String(newW));
    fo.setAttribute('height', String(newH));
    fo.setAttribute('x', String(-padding));
    fo.setAttribute('y', String(-padding));

    if (labelG) {
      labelG.setAttribute('transform', `translate(${-newW / 2}, ${-newH / 2})`);
    }
  });
}

/**
 * Renders Mermaid diagram syntax as an SVG visualization.
 * Used for ```mermaid code blocks - line graphs, bar charts, flowcharts, etc.
 * Click to open in fullscreen viewer with zoom/pan.
 */
function sanitizeForFilename(title) {
  if (!title || typeof title !== 'string') return 'Untitled';
  return title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').trim() || 'Untitled';
}

const MermaidDiagram = ({ children, darkMode = false, onNodeClick, onEdgeClick, chatTitle }) => {
  const containerRef = useRef(null);
  const inlineRef = useRef(null);
  const fullscreenRef = useRef(null);
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const idRef = useRef(`mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const diagramBg = darkMode ? '#0f172a' : '#f8fafc';

  const handleExportPng = async () => {
    const el = fullscreenRef.current?.querySelector?.('svg') || inlineRef.current?.querySelector?.('svg');
    if (!el) return;
    setExporting(true);
    try {
      const clone = el.cloneNode(true);
      const vb = el.getAttribute('viewBox');
      if (vb) {
        const [,, vw, vh] = vb.split(/[\s,]+/).map(Number);
        const scale = Math.max(2, Math.ceil(1600 / Math.max(vw, 1)));
        clone.setAttribute('width', String(vw * scale));
        clone.setAttribute('height', String(vh * scale));
      } else {
        clone.setAttribute('width', '3200');
        clone.setAttribute('height', '2400');
      }
      clone.style.background = diagramBg;
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-99999px';
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      const dataUrl = await toPng(clone, { pixelRatio: 1, backgroundColor: diagramBg });
      document.body.removeChild(wrapper);
      const a = document.createElement('a');
      a.href = dataUrl;
      const baseName = `Midus-${sanitizeForFilename(chatTitle)}-Visual`;
      a.download = `${baseName}.png`;
      a.click();
    } catch (err) {
      console.error('Export PNG failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleExportSvg = () => {
    if (!svg) return;
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${svg}`], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = `Midus-${sanitizeForFilename(chatTitle)}-Visual`;
    a.download = `${baseName}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDiagramClick = (e) => {
    const target = e.target;
    const node = target.closest?.('.node');
    if (node && onNodeClick) {
      const id = node.id;
      if (id) {
        onNodeClick(id);
        e.stopPropagation();
      }
      return;
    }
    const edgeLabel = target.closest?.('.edgeLabel');
    const edgePath = target.closest?.('.edgePaths path');
    if ((edgeLabel || edgePath) && onEdgeClick) {
      const edgeText = edgeLabel?.textContent?.trim() || edgePath?.id || '';
      onEdgeClick(edgeText);
      e.stopPropagation();
    }
  };

  const diagramCode = typeof children === 'string'
    ? children
    : (Array.isArray(children) ? children.join('') : String(children || '')).trim();

  useEffect(() => {
    if (!diagramCode || !containerRef.current) return;

    let cancelled = false;

    const render = async () => {
      try {
        setError(null);
        const darkVars = darkMode ? {
          darkMode: true,
          background: '#0f172a',
          primaryColor: PASTEL_HEX[0],
          primaryTextColor: '#1e293b',
          primaryBorderColor: '#c4a494',
          secondaryColor: PASTEL_HEX[1],
          secondaryTextColor: '#1e293b',
          tertiaryColor: PASTEL_HEX[2],
          tertiaryTextColor: '#1e293b',
          lineColor: '#64748b',
          textColor: '#e2e8f0',
          nodeTextColor: '#1e293b',
          mainBkg: PASTEL_HEX[0],
          nodeBkg: PASTEL_HEX[0],
          edgeLabelBackground: PASTEL_HEX[3],
          noteBkgColor: PASTEL_HEX[4],
          noteTextColor: '#1e293b',
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScale${i}`, PASTEL_HEX[i]])
          ),
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScaleInv${i}`, '#1e293b'])
          ),
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScaleLabel${i}`, '#1e293b'])
          ),
        } : {
          darkMode: false,
          background: '#f8fafc',
          primaryColor: PASTEL_HEX[0],
          primaryTextColor: '#1e293b',
          primaryBorderColor: '#c4a494',
          secondaryColor: PASTEL_HEX[1],
          secondaryTextColor: '#1e293b',
          tertiaryColor: PASTEL_HEX[2],
          tertiaryTextColor: '#1e293b',
          lineColor: '#64748b',
          textColor: '#1e293b',
          nodeTextColor: '#1e293b',
          mainBkg: PASTEL_HEX[0],
          nodeBkg: PASTEL_HEX[0],
          edgeLabelBackground: PASTEL_HEX[3],
          noteBkgColor: PASTEL_HEX[4],
          noteTextColor: '#1e293b',
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScale${i}`, PASTEL_HEX[i]])
          ),
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScaleInv${i}`, '#1e293b'])
          ),
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [`cScaleLabel${i}`, '#1e293b'])
          ),
        };

        const fontFamily = typeof document !== 'undefined'
          ? (getComputedStyle(document.documentElement).getPropertyValue('--chat-font-family').trim() || 'system-ui, -apple-system, sans-serif')
          : 'system-ui, -apple-system, sans-serif';
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          themeVariables: { ...darkVars, fontFamily },
          securityLevel: 'loose',
          flowchart: { useMaxWidth: true },
          sequence: { useMaxWidth: true },
          xychart: { useMaxWidth: true },
        });

        const { svg: rawSvg } = await mermaid.render(idRef.current, diagramCode);
        if (!cancelled) setSvg(rawSvg);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to render diagram');
          setSvg(null);
        }
      }
    };

    render();
    return () => { cancelled = true; };
  }, [diagramCode, darkMode]);

  useEffect(() => {
    if (!svg) return;
    const timer = setTimeout(() => {
      applyPastelColorsToNodes(inlineRef.current);
      applyPastelColorsToNodes(fullscreenRef.current);
      fixSvgContrastInDom(inlineRef.current, diagramBg);
      fixSvgContrastInDom(fullscreenRef.current, diagramBg);
      applySquircleToNodes(inlineRef.current);
      applySquircleToNodes(fullscreenRef.current);
      applyEdgeLabelStyle(inlineRef.current);
      applyEdgeLabelStyle(fullscreenRef.current);
    }, 50);
    return () => clearTimeout(timer);
  }, [svg, fullscreenOpen, diagramBg]);

  if (error) {
    return (
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
        <p className="font-medium">Diagram could not be rendered</p>
        <p className="mt-1 text-xs opacity-80">{error}</p>
        <pre className="mt-2 overflow-x-auto rounded bg-black/10 p-2 text-xs dark:bg-white/10">
          {diagramCode}
        </pre>
      </div>
    );
  }

  if (!svg) {
    const fontFamily = typeof document !== 'undefined'
      ? (getComputedStyle(document.documentElement).getPropertyValue('--chat-font-family').trim() || 'system-ui, -apple-system, sans-serif')
      : 'system-ui, -apple-system, sans-serif';
    const maskSvg = `<svg width="200" height="20" viewBox="0 0 200 20" xmlns="http://www.w3.org/2000/svg"><text x="0" y="15" font-family="${fontFamily.replace(/"/g, '&quot;')}" font-size="14" fill="black">Rendering diagram...</text></svg>`;
    return (
      <div
        ref={containerRef}
        className="flex min-h-[120px] items-center justify-center rounded-lg"
        style={{ fontFamily: 'var(--chat-font-family)' }}
      >
        <SkeletonTheme
          baseColor={darkMode ? '#666666' : '#6b7280'}
          highlightColor={darkMode ? '#999999' : '#9ca3af'}
        >
          <div style={{ width: 200, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div
              style={{
                width: 200,
                height: 20,
                maskImage: `url("data:image/svg+xml,${encodeURIComponent(maskSvg)}")`,
                WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(maskSvg)}")`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            >
              <Skeleton width={200} height={20} style={{ borderRadius: 0, display: 'block', margin: 0, padding: 0, lineHeight: 1, verticalAlign: 'top' }} />
            </div>
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  const fullscreenModal = fullscreenOpen && createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label="Diagram fullscreen viewer"
    >
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        <button
          type="button"
          className="rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
          onClick={() => setFullscreenOpen(false)}
          aria-label="Close"
        >
          Close
        </button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col p-4 pt-14" onClick={(e) => e.target === e.currentTarget && setFullscreenOpen(false)}>
        <TransformWrapper
          initialScale={1}
          minScale={0.2}
          maxScale={10}
          centerZoomedOut
          limitToBounds={false}
          doubleClick={{ mode: 'reset' }}
          wheel={{ step: 0.15 }}
          pinch={{ step: 10 }}
          onInit={({ centerView, instance }) => {
            setTimeout(() => {
              const wrapper = instance.wrapperComponent;
              const content = instance.contentComponent;
              if (!wrapper || !content) { centerView(1, 0); return; }
              const svgEl = content.querySelector('svg');
              if (!svgEl) { centerView(1, 0); return; }
              const bbox = svgEl.getBoundingClientRect();
              const wbox = wrapper.getBoundingClientRect();
              const scaleX = (wbox.width - 48) / bbox.width;
              const scaleY = (wbox.height - 80) / bbox.height;
              const fitScale = Math.min(scaleX, scaleY);
              centerView(Math.max(fitScale, 0.5), 0);
            }, 100);
          }}
        >
          {({ zoomIn, zoomOut, resetTransform, centerView, instance }) => (
            <>
              <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 flex-wrap justify-center gap-2 rounded-lg bg-black/60 p-2">
                <button type="button" className="rounded px-3 py-1.5 text-white hover:bg-white/20" onClick={() => zoomOut()} aria-label="Zoom out">−</button>
                <button type="button" className="rounded px-3 py-1.5 text-white hover:bg-white/20" onClick={() => {
                  const wrapper = instance.wrapperComponent;
                  const content = instance.contentComponent;
                  const svgEl = content?.querySelector('svg');
                  if (wrapper && svgEl) {
                    const bbox = svgEl.getBoundingClientRect();
                    const wbox = wrapper.getBoundingClientRect();
                    const scale = instance.transformState.scale;
                    const naturalW = bbox.width / scale;
                    const naturalH = bbox.height / scale;
                    const scaleX = (wbox.width - 48) / naturalW;
                    const scaleY = (wbox.height - 80) / naturalH;
                    centerView(Math.max(Math.min(scaleX, scaleY), 0.5), 300);
                  } else {
                    resetTransform();
                  }
                }} aria-label="Reset zoom">Reset</button>
                <button type="button" className="rounded px-3 py-1.5 text-white hover:bg-white/20" onClick={() => zoomIn()} aria-label="Zoom in">+</button>
                <button type="button" className="rounded px-3 py-1.5 text-white hover:bg-white/20" onClick={handleExportSvg} aria-label="Export SVG">SVG</button>
                <button type="button" className="rounded px-3 py-1.5 text-white hover:bg-white/20 disabled:opacity-50" onClick={handleExportPng} disabled={exporting} aria-label="Export PNG">{exporting ? '…' : 'PNG'}</button>
              </div>
              <TransformComponent
                wrapperClass="mermaid-fullscreen-wrapper"
                contentClass="mermaid-fullscreen-content"
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: 'max-content', height: 'max-content' }}
              >
                <div
                  ref={fullscreenRef}
                  className="mermaid-diagram-fullscreen cursor-default"
                  onClick={handleDiagramClick}
                  dangerouslySetInnerHTML={{ __html: svg }}
                  role="presentation"
                />
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={containerRef}
        role="button"
        tabIndex={0}
        className="mermaid-diagram my-2 flex min-w-0 cursor-pointer justify-center overflow-x-auto p-5 transition-all hover:opacity-95"
        onClick={() => setFullscreenOpen(true)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setFullscreenOpen(true)}
        aria-label="Click to view fullscreen"
      >
        <div ref={inlineRef} className="mermaid-diagram-inline w-full cursor-pointer" onClick={handleDiagramClick} dangerouslySetInnerHTML={{ __html: svg }} role="presentation" />
      </div>
      {fullscreenModal}
    </>
  );
};

export default MermaidDiagram;
